/**
 * Who is signed in, shared by every page.
 *
 * One fetch of `GET /api/session` per page load, held in a context, because
 * the navbar, the guards and the pages all need the same answer and asking
 * three times would show three different loading states. `refresh()` is for
 * the flows that change the answer without a navigation (signing out).
 */

import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AccountInfo, OAuthProviderId } from "@xtract-bot/embellisher-schema";
import { api, errorMessage } from "./api";

export interface Session {
  /** null once loaded and nobody is signed in; undefined while the first fetch is in flight. */
  account: AccountInfo | null;
  providers: OAuthProviderId[];
  loading: boolean;
  /** Set when the session probe itself failed — a deployment problem, not a signed-out user. */
  error: string | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [providers, setProviders] = useState<OAuthProviderId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const result = await api.session(signal);
      if (signal?.aborted) return;
      setAccount(result.account);
      setProviders(result.providers);
      setError(null);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setAccount(null);
      setError(errorMessage(cause));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const signOut = useCallback(async () => {
    await api.signOut();
    // A full navigation rather than a state change: the cookie is gone, and
    // every page's data was fetched under it.
    window.location.assign("/");
  }, []);

  const value = useMemo<Session>(
    () => ({ account, providers, loading, error, refresh: () => load(), signOut }),
    [account, providers, loading, error, load, signOut],
  );

  return createElement(SessionContext.Provider, { value }, children);
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession was called outside <SessionProvider>; wrap the router in it (see main.tsx).");
  return session;
}

/** `/signin?next=<here>` — where an unauthenticated page sends the visitor. */
export function signInHref(next?: string): string {
  const target = next ?? `${window.location.pathname}${window.location.search}`;
  return target === "/" ? "/signin" : `/signin?next=${encodeURIComponent(target)}`;
}
